import KMM from '@/lib/kmm'
import { formatPrecipitationAmount } from './precipitation/format'
import type { RestOfDayHourly } from '@/services/components/strategies/weather/carousel/WeatherCardCarousel/utils'
import { DayboxHourlyEntry } from '@/services/api/bff/weather/dtos/Dayboxes.dto'

/**
 * Formats a date into the specified format string.
 * @param date - The date object to format.
 * @param format - The format string, e.g., 'YYYY-MM-DDTHH:mm:ss'.
 * @returns The formatted date string.
 */
export function formatDate(date: Date, format: string): string {
  const pad = (num: number): string => num.toString().padStart(2, '0')
  return format
    .replace('YYYY', date.getUTCFullYear().toString())
    .replace('MM', pad(date.getUTCMonth() + 1))
    .replace('DD', pad(date.getUTCDate()))
    .replace('HH', pad(date.getUTCHours()))
    .replace('mm', pad(date.getUTCMinutes()))
    .replace('ss', pad(date.getUTCSeconds()))
}

/**
 * Describes the weather conditions for the day.
 * @param hourData - Hourly data for the day.
 * @param amountOfRain - Amount of rain.
 * @param weatherIcon - Weather icon identifier.
 * @param windIntensity - Wind intensity.
 * @param sunhours - Amount of sun hours.
 * @param tMax - Maximum temperature.
 * @param maxTempYesterday - Yesterday's maximum temperature.
 * @returns Description of the day's weather.
 */
export function getDayDescription(
  hourData: DayboxHourlyEntry[],
  amountOfRain: number,
  weatherIcon: number,
  windIntensity: number,
  sunhours: number,
  tMax: number,
  maxTempYesterday: number,
): string {
  if (!hourData || hourData.length === 0) return '…'

  const firstHourDate = new Date(hourData[0].dateTime)
  const formattedDate = formatDate(firstHourDate, 'YYYY-MM-DDTHH:mm:ss')
  const symbolString = getWeatherSymbol(hourData as any)

  return KMM.Weather.getWeatherInfoForWholeDay(
    amountOfRain,
    symbolString,
    windIntensity,
    sunhours,
    tMax,
    maxTempYesterday,
    formattedDate,
  )
}

/**
 * Describes today's weather conditions.
 * @param todayHours - Today's hourly data.
 * @param sunrise - Sunrise time as ISO string.
 * @param sunset - Sunset time as ISO string.
 * @param data - Full daybox data including attributes.
 * @param maxTempYesterday - Yesterday's max temperature.
 * @param refresh - Flag to indicate if data should be refreshed.
 * @param windDirection - Wind direction.
 * @param currentDate - Current date used for fallback (UTC).
 * @returns Description of today's weather or placeholder.
 */
export function getDayDescriptionToday(
  todayHours:
    | Array<DayboxHourlyEntry & { localDateTime: string }>
    | Array<RestOfDayHourly & { localDateTime: string }>,
  sunrise: string,
  sunset: string,
  data: { attributes: { tMax: number } },
  maxTempYesterday: number | null,
  refresh: boolean,
  windDirection: number,
  currentDate: Date = new Date(),
): string {
  if (!refresh || !todayHours || todayHours.length === 0) return '…'

  const times = todayHours.map((h) =>
    formatDate(new Date(h.localDateTime), 'HH:00'),
  )
  const symbols = todayHours.map((h) => h.symbol)
  const windSpeeds = todayHours.map((h) => h.windSpeed)
  const windGusts = todayHours.map((h) => h.windGusts)
  const sunMinutes = todayHours.map((h) => h.sunMinutes)
  const tMax = data.attributes?.tMax ?? 0
  const formattedSunrise = formatDate(new Date(sunrise), 'HH:mm:00')
  const formattedSunset = formatDate(new Date(sunset), 'HH:mm:00')
  const formattedNow = formatDate(currentDate, 'YYYY-MM-DDTHH:mm:00')

  return KMM.Weather.getWeatherInfoForToday(
    times,
    symbols,
    windSpeeds,
    windDirection,
    windGusts,
    sunMinutes,
    formattedSunrise,
    formattedSunset,
    tMax,
    maxTempYesterday,
    formattedNow,
  )
}

/**
 * Calculates the maximum temperature from used hour data.
 * @param usedHourData - The hour data used for calculation.
 * @returns The maximum temperature string.
 */
export function calculateMaxTemp(usedHourData: DayboxHourlyEntry[]): string {
  return usedHourData && usedHourData.length
    ? KMM.Temperature.getTempMax(usedHourData.map((h) => h.temp))
    : '??'
}

/**
 * Calculates the minimum temperature from used hour data.
 * @param usedHourData - The hour data used for calculation.
 * @returns The minimum temperature string.
 */
export function calculateMinTemp(usedHourData: DayboxHourlyEntry[]): string {
  return usedHourData && usedHourData.length
    ? KMM.Temperature.getTempMin(usedHourData.map((h) => h.temp))
    : '??'
}

/**
 * Fetches alert data for a specific local time range in ISO8601 format.
 * @param {string} from - Start time in ISO8601 format.
 * @param {string} till - End time in ISO8601 format.
 * @returns {string} Alert data for the specified time range.
 */
export function getAlertDataByLocalTime(from: string, till: string): string {
  return KMM.Alert.getAlertDataByLocalTimeISO8601(from, till)
}

/**
 * Provides current weather condition temperatures.
 * @param temp - Current temperature.
 * @param apparentTemp - Apparent temperature.
 * @returns Display string for current condition temperatures.
 */
export function getCurrentConditionTemperature(
  temp: number,
  apparentTemp: number,
): string {
  return (
    KMM.Temperature.getCurrentConditionTemperatures(temp, apparentTemp)
      ?.tApparentDisplay ?? ''
  )
}

/**
 * Describes one hour's weather info.
 */
export function getWeatherInfoForOneHour(hour: DayboxHourlyEntry): string {
  return KMM.Weather.getWeatherInfoForOneHour(
    hour.temp,
    hour.tApparent,
    hour.symbol,
    hour.precipitation,
    hour.precipitationChance,
    hour.windSpeed,
    hour.windGusts,
    hour.uvIndex,
  )
}

/**
 * Calculates the aggregate weather symbol from hour data.
 * @param hourData - The hour data used for calculation.
 * @returns The aggregate weather symbol identifier.
 */
export function getWeatherSymbol(
  hourData: DayboxHourlyEntry[] | RestOfDayHourly[],
): string {
  return hourData && hourData.length
    ? KMM.Weather.getAggregateSymbolMapping(hourData.map((h) => h.symbol))
    : '-1'
}

/**
 * Calculates the probability of precipitation.
 * @param hourData - The hour data used for calculation.
 * @returns Precipitation probability string.
 */
export function getChanceOfRain(
  hourData: Array<{ precipitationChance: number | string }>,
): string {
  if (!hourData || hourData.length === 0) return '?'
  const chances = hourData.map((h) =>
    typeof h.precipitationChance === 'string'
      ? parseFloat(h.precipitationChance) / 100
      : h.precipitationChance / 100,
  )
  return KMM.Precipitation.getPrecipitationProbability(chances)
}

/**
 * Calculates the total amount of precipitation.
 * @param hourData - The hour data used for calculation.
 * @returns Total precipitation amount string.
 */
export function getAmountOfRain(hourData: DayboxHourlyEntry[]): string {
  if (!hourData || hourData.length === 0) return '?'
  const amounts = hourData.map((h) =>
    typeof h.precipitation === 'string'
      ? parseFloat(h.precipitation)
      : h.precipitation,
  )
  return KMM.Precipitation.getPrecipitationAmount(amounts)
}

/**
 * Calculates total sun minutes from hour data.
 * @param hourData - The hour data used for calculation.
 * @returns Total sun minutes string.
 */
export function getSunMinutes(hourData: DayboxHourlyEntry[]): string {
  if (!hourData || hourData.length === 0) return '?'
  const minutes = hourData.map((h) => h.sunMinutes)
  return KMM.SunHours.getSunHours(minutes)
}

/**
 * Calculates the total sun hours from the hour data.
 * @param {amount} amount of precipitation.
 * @param {symbol} symbol symbol in api3-format.
 * @returns {string} precipitation information.
 */
export function getPrecipitationInfoNotForToday(
  amount: number,
  symbol: string,
): string {
  return KMM.Precipitation.getPrecipitationInfoNotForToday(amount, symbol)
}

/**
 * Calculates the total sun hours from the hour data.
 * @param {minutes} sun minutes per hour.
 * @param {dateTimeString} Date represented as String in format "yyyy-MM-ddTHH:mm:ss".
 * @returns {string} sun information text.
 */
export function getSunHoursInfoForToday(
  minutes: number[],
  dateTimeString: string,
): string {
  const date = formatDate(new Date(dateTimeString), 'YYYY-MM-DDTHH:mm:ss')
  return KMM.SunHours.getSunHoursInfoForToday(minutes, date)
}

/**
 * Calculates the total sun hours from the hour data.
 * @param {sunHours} sun hours for the whole day
 * @param {dateTimeString} Date represented as String in format "yyyy-MM-ddTHH:mm:ss".
 * @returns {string} sun information text.
 */
export function getSunHoursInfoNotForToday(
  sunHours: number,
  dateTimeString: string,
): string {
  const date = formatDate(new Date(dateTimeString), 'YYYY-MM-DDTHH:mm:ss')
  return KMM.SunHours.getSunHoursInfoNotForToday(sunHours, date)
}

/**
 * Calculates the total sun hours from the hour data.
 * @param {speed} Wind speed for whole day
 * @param {isNewSentence} Output will be a standalone sentence without "." at the end
 * @returns {string} wind forecast text.
 */
export function getWindInfoNotForToday(
  speed: number,
  isNewSentence: boolean,
): string {
  return KMM.Wind.getWindInfoNotForToday(speed, isNewSentence)
}

/**
 * @param {number} minTemperature
 * @param {number} maxTemperature
 * @param {number} maxTemperaturePreviousDay
 *
 * @return Temperature description with min/max temperature and difference to previous day
 */
export function getTemperatureDescription(
  minTemperature: number,
  maxTemperature: number,
  maxTemperaturePreviousDay: number,
): string {
  return KMM.Diagram.getTemperatureDescription(
    minTemperature,
    maxTemperature,
    maxTemperaturePreviousDay,
  )
}

/**
 * @param {array[string]} hours: remaining hours of the day including the current hour
 * @param {array[string]} symbols: associated symbols for the remaining hours (see hours)
 * @param {array[number]} remainingPrecip: precipitation values of remaining hours
 *
 * @return Precipitation description for today
 */
export function getPrecipitationDescriptionForToday(
  hours: string[],
  symbols: string[],
  remainingPrecip: number[],
): string {
  const base = KMM.Diagram.getPrecipitationDescriptionForToday(hours, symbols)
  const total = remainingPrecip.reduce((sum, cur) => sum + cur, 0)
  return (
    base +
    (total === 0
      ? ''
      : ` Noch erwartete Menge: ${formatPrecipitationAmount(total)} l/m²`)
  )
}

/**
 * @param {number} precipitationAmount: amount of precipitation
 * @param {string} symbol: symbol in api3-format
 *
 * @return Precipitation description not for today
 */
export function getPrecipitationDescriptionNotForToday(
  precipitationAmount: number,
  symbol: string,
): string {
  const base = KMM.Diagram.getPrecipitationDescriptionNotForToday(
    precipitationAmount,
    symbol,
  )
  return (
    base +
    (precipitationAmount === 0
      ? ''
      : ` Die Niederschlagsmenge liegt bei ${formatPrecipitationAmount(precipitationAmount)} l/m²`)
  )
}

/**
 * @param {array[string]} hours: remaining hours of the day including the current hour
 * @param {array[number]} windIntensities
 * @param {array[number]} gustIntensities
 *
 * @return Wind description with max intensity and gust
 */
export function getWindDescription(
  hours: string[],
  windIntensities: number[],
  gustIntensities: number[],
): string {
  return KMM.Diagram.getWindDescription(hours, windIntensities, gustIntensities)
}

/**
 * @param {array[number]} humidity
 *
 * @return Air humidity description
 */
export function getAirHumidityDescription(humidity: number[]): string {
  return KMM.Diagram.getAirHumidityDescription(humidity)
}

/**
 * @param {array[number]} pressure
 *
 * @return Air pressure description
 */
export function getAirPressureDescription(pressure: number[]): string {
  return KMM.Diagram.getAirPressureDescription(pressure)
}

/**
 * Provides a localized sunrise and sunset countdown.
 *
 * @param sunrise - ISO string for sunrise time.
 * @param sunset - ISO string for sunset time.
 * @param localCurrentTime - Current local time as a Date object.
 * @returns A string describing how many minutes until sunrise and sunset,
 *          e.g. "Sonnenaufgang in 5 Minuten. Sonnenuntergang in 10 Minuten."
 */
export function getSunriseSunsetInfo(
  sunrise: string,
  sunset: string,
  localCurrentTime: Date,
): string {
  const minutesUntil = (time: Date) =>
    Math.floor((time.getTime() - localCurrentTime.getTime()) / 60000)

  const ms = minutesUntil(new Date(sunrise))
  const mt = minutesUntil(new Date(sunset))
  let info = ''
  if (ms === 1) info += 'Sonnenaufgang in einer Minute.'
  else if (ms >= 2 && ms <= 59) info += `Sonnenaufgang in ${ms} Minuten.`
  if (mt === 1) info += 'Sonnenuntergang in einer Minute.'
  else if (mt >= 2 && mt <= 59) info += `Sonnenuntergang in ${mt} Minuten.`
  return info
}

/**
 * Weather info for day segments.
 */
export function getWeatherInfoForDaySegments(
  hoursForDaySegment: string[],
  symbolsForDaySegment: string[],
  temperatureForDaySegment: number[],
  precipitationAmountForDaySegment: number[],
  precipitationChanceForDaySegment: number[],
): string {
  return KMM.Weather.getWeatherInfoForDaySegments(
    hoursForDaySegment,
    symbolsForDaySegment,
    temperatureForDaySegment,
    precipitationAmountForDaySegment,
    precipitationChanceForDaySegment,
  )
}

type SymbolMappingConfig = {
  api3: string | null
  short: string
  legacySymbol: boolean
  img: string
  alt: string
  emoji: string
  condition: string
  layers: any[]
  layersCondensed: string
  title: string
  dominantPrecipitationType: string
  dominantAbsentPrecipitationType: string
  showRainRadar: boolean
  isRainy: boolean
  isDay: boolean
  weight: number
}

/**
 * Aggregate symbol mapping utility.
 */
export function getAggregateSymbolMapping(
  symbols: string[],
  symbolMappings: SymbolMappingConfig[],
): string {
  if (!symbols || symbols.length === 0) return '1'
  const isDay = symbols.some((s) => s.length < 3)
  let ints: number[] = []
  if (isDay) {
    ints = symbols.filter((s) => s.length < 3).map((s) => parseInt(s, 10))
  } else {
    ints = symbols.map((s) =>
      s.length > 2 ? parseInt(s.slice(-2), 10) : parseInt(s, 10),
    )
  }
  const max = Math.max(...ints)
  const occurrences = new Array(max).fill(0)
  ints.forEach((i) => occurrences[i - 1]++)
  const weights = occurrences.map((occ, idx) => {
    const cfg = symbolMappings.find((m) => m.api3 === (idx + 1).toString())
    return (cfg?.weight ?? 0) * occ
  })
  const idxMax = weights.reduce(
    (bestIdx, w, idx) => (w > weights[bestIdx] ? idx : bestIdx),
    0,
  )
  const chosen = symbolMappings.find((m) => m.api3 === (idxMax + 1).toString())
  if (isDay) return chosen?.api3 ?? ''
  return (100 + parseInt(chosen?.api3 ?? '-1', 10)).toString()
}

/**
 * Alias for alert fetch.
 */
export function fromToString(from: string, to: string): string {
  return KMM.Alert.getAlertDataByLocalTimeISO8601(from, to)
}
